import { useEffect, useRef, useState } from "react";
import {
  isFixtureMode,
  resolveDecoder,
  type CodeDecoder,
  type ScanFormat,
  type ScanMode,
} from "./scan";

export interface QrScannerState {
  /** The decoded text, most recent confident hit (updated as labels change). */
  result: string | null;
  /** Symbology of `result`; `null` until the first decode (scanner-surface 06). */
  format: ScanFormat | null;
  /** Camera could not start (fixture mode never sets this). */
  cameraError: boolean;
  /** Whether the camera loop is currently running (drives overlay pulse). */
  scanning: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
}

/**
 * Continuous scanning hook (ticket 09). The backend depends on `mode`:
 * `barcode` uses Quagga2, `qr`/`both` use zxing-wasm. Each backend owns its
 * own persistent frame loop + camera stream, started once while `open`, and
 * reports every *new* decoded value via a callback until `cleanup()` — so
 * re-aiming at a different label refreshes `result` without reopening the
 * camera. No timer poll: results arrive from each decoder's native callback.
 * In fixture mode (unit tests / e2e) a synthetic decoder is polled on a timer
 * to emulate the same continuous stream; with no decoder and no fixture the
 * real camera + mode decoder runs (and surfaces a camera error in
 * environments without a working camera).
 *
 * The decoded symbology is exposed next to the text (scanner-surface 06): a
 * fixture that returns a bare string reports `unknown`.
 */
export function useQrScanner(
  decoder?: CodeDecoder,
  open = true,
  mode: ScanMode = "both",
): QrScannerState {
  const [result, setResult] = useState<string | null>(null);
  const [format, setFormat] = useState<ScanFormat | null>(null);
  const [cameraError, setCameraError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cancelledRef = useRef(true);

  useEffect(() => {
    cancelledRef.current = !open;
    if (!open) {
      setResult(null);
      setFormat(null);
      setCameraError(false);
      setScanning(false);
      return;
    }

    let cancelled = false;
    const markCancelled = () => {
      cancelled = true;
      cancelledRef.current = true;
    };
    const eff = resolveDecoder(decoder);
    const synthetic = Boolean(decoder) || isFixtureMode();

    const apply = (decoded: string | { text: string; format: ScanFormat } | null) => {
      if (!decoded) return;
      if (typeof decoded === "string") {
        setResult(decoded);
        setFormat("unknown");
      } else {
        setResult(decoded.text);
        setFormat(decoded.format);
      }
    };

    if (synthetic) {
      // Fixture/e2e seam: emulate the live camera loop by polling the
      // synthetic decoder; repeated same-value results are no-ops (React
      // state equality), new values refresh the caller.
      setScanning(true);
      const timer = setInterval(async () => {
        if (cancelled) return;
        const decoded = await eff({} as HTMLVideoElement);
        if (!cancelled && decoded) apply(decoded);
      }, 250);
      return () => {
        markCancelled();
        setScanning(false);
        clearInterval(timer);
      };
    }

    let release: (() => void) | null = null;

    (async () => {
      try {
        const { createDecoder } = await import("./decoders/create");
        const instance = await createDecoder(mode);
        if (cancelled) {
          instance.cleanup();
          return;
        }
        release = () => instance.cleanup();

        // The decoder owns the camera stream and its continuous frame loop;
        // `start` resolves once the camera is live and keeps emitting newer
        // values via onResult until cleanup. It rejects when the camera
        // cannot start (mapped to the "camera unavailable" state below).
        setScanning(true);
        await instance.start(videoRef.current!, (decoded) => {
          if (!cancelled) apply(decoded);
        });
      } catch {
        if (!cancelled) {
          setCameraError(true);
          setScanning(false);
        }
      }
    })();

    return () => {
      markCancelled();
      setScanning(false);
      release?.();
    };
  }, [decoder, open, mode]);

  return { result, format, cameraError, scanning, videoRef };
}
